// components/CardFocus.js
export default class CardFocus {

  constructor(opts = {}) {

    this.root = opts.root || document;
    this.deck = opts.deck || document.querySelector('#deck');

    // Fades the page to white and back around every open, swap and close.
    // Without one those changes happen instantly.
    this.transition = opts.transition || {
      cover: async () => {},
      reveal: async () => {},
      ready: async () => {},
    };

    // The tab title follows the open project. Passed in so the tab joust,
    // which borrows the title for its frames, knows what to hand back.
    this.getTitle = opts.getTitle || (() => document.title);
    this.setTitle = opts.setTitle || ((t) => { document.title = t; });

    this.overlay = document.querySelector('.cardfocus') || null;
    this._hasPreRenderedOverlay = !!this.overlay;

    // Selectors that match your case study template
    this.visualSelector  = opts.visualSelector  || '.visual-content';
    this.panelSelector   = opts.panelSelector   || 'aside';
    this.relatedSelector   = opts.relatedSelector   || '.related';
    this.titleSelector   = opts.titleSelector   || 'h1, .entry-title, [data-title]';
    this.fallbackRootSel = opts.contentSelectorFallback || 'main, .content, article, .page';
    this.stripSelectors  = opts.stripSelectors  || ['header', '.head', 'nav', '.tabs', '.deck', '.col'];

    // Layout & timings (tweak to taste)
    this.gapPx = typeof opts.gapPx === 'number' ? opts.gapPx : 16;

    // History
    this._onPopState = this._onPopState.bind(this);
    window.addEventListener('popstate', this._onPopState);

    // Bind events
    this.onClick = this.onClick.bind(this);
    this.onKey   = this.onKey.bind(this);
    this.root.addEventListener('click', this.onClick);
    this.root.addEventListener('keydown', this.onKey);

    // A case study page loaded directly already has its overlay
    if (this.overlay) this._fitPanel(this.overlay);
  }

  onKey(e) {
    const card = e.target?.closest?.('.card');

    // Only when the card itself has focus. A focused link inside it (e.g. in
    // rich-text copy) must keep its native Enter/Space behaviour, and must
    // never be opened as a case study.
    if ((e.key === 'Enter' || e.key === ' ') && card && e.target === card) {
      const link = card.querySelector('.card__link');
      if (!link) return;
      e.preventDefault();
      this._activateFromCard(card, link.href);
      return;
    }
    if (e.key === 'Escape') this.close();
  }

  onClick(e) {
    // Back button/link inside overlay
    if (this.overlay) {
        const backBtn = e.target.closest('.cardfocus__back');
        if (backBtn) {
        // If it's rendered as <button>, treat as history back
        if (backBtn.tagName === 'BUTTON') {
            e.preventDefault();
            window.history.back();
            return;
        }
        // If it's <a href="/">, allow normal navigation (Home)
        return;
        }
    }

    // Card activation
    const link = e.target.closest('.card__link');
    if (!link) return;
    e.preventDefault();

    const card = link.closest('.card');
    const url = link.getAttribute('href');
    if (!card) return;

    // If overlay is already open, just swap content
    if (this.overlay) {
        this._swapTo(card, url);
        return;
    }

   
    // If SSR pre-rendered .cardfocus exists (direct entry landing), adopt + swap
    const pre = document.querySelector('.cardfocus');
    if (pre) {
        this.overlay = pre;
        this._hasPreRenderedOverlay = true;
        this._swapTo(card, url);
        return;
    }
    // Otherwise (first open from grid), activate with zoom animation
    this._activateFromCard(card, url);
    }


  async _swapTo(card, url) {
    if (!this.overlay || !card || !url) return;

    // Fade to white, swap everything while hidden, fade back in as one.
    await this.transition.cover();
    try {
      window.history.pushState({ cardfocus: true }, '', url);
    } catch (_) {}

    await this._populate(card, url);
    await this._revealOverlay();
  }

  /** Fill the open overlay from the project page at `url`. */
  async _populate(card, url) {

    // Grab current overlay targets
    const ov         = this.overlay;
    const left       = ov.querySelector('.cardfocus__left');       // scrollable
    const leftInner  = ov.querySelector('.cardfocus__leftInner');  // content
    const panelHost  = ov.querySelector('.cardfocus__content');    // aside content
    const relatedBox = ov.querySelector('.related');               // related grid

    // Reset the mobile info toggle (in case it was active on the previous project)
    const infoToggle = ov.querySelector('[data-info-toggle]');
    if (infoToggle) {
      ov.querySelectorAll('.content-wrap.is-info-injected').forEach((el) => el.remove());
      if (infoToggle.parentElement !== ov) ov.appendChild(infoToggle);
      infoToggle.classList.remove('is-active');
      infoToggle.setAttribute('aria-expanded', 'false');
      const span = infoToggle.querySelector('span') || infoToggle;
      span.textContent = 'i';
      infoToggle.setAttribute('aria-label', 'Project info');
    }

    // Fetch & parse the new page
    let doc = null;
    try {
        const html = await this._fetchHTML(url);
        doc = new DOMParser().parseFromString(html, 'text/html');
    } catch (err) {
        console.warn('[CardFocus] swap fetch failed:', err);
        return;
    }

    // ----------------------------
    // RIGHT (aside / panel)
    // ----------------------------
    const asideFromDoc = doc.querySelector(this.panelSelector);
    if (asideFromDoc) {
        const frag = document.createDocumentFragment();
        [...asideFromDoc.childNodes].forEach(n => frag.appendChild(n.cloneNode(true)));
        panelHost.innerHTML = '';
        panelHost.appendChild(frag);
    } else {
        // Fallback: strip generic root
        const genericRoot = doc.querySelector(this.fallbackRootSel) || doc.body;
        this._strip(genericRoot, this.stripSelectors);
        const frag = document.createDocumentFragment();
        [...genericRoot.childNodes].forEach(n => frag.appendChild(n.cloneNode(true)));
        panelHost.innerHTML = '';
        panelHost.appendChild(frag);
    }

    this._syncInfoState(ov);
    this._fitPanel(ov);

    const pageTitle = doc.querySelector('title')?.textContent?.trim();
    if (pageTitle) this.setTitle(pageTitle);

    // Carry the project's fun cursor onto its page (see FunCursor)
    const funImage = doc.querySelector('.cardfocus')?.dataset.funImage || card.dataset.funImage;
    if (funImage) {
      ov.dataset.funImage = funImage;
      new Image().src = funImage; // ready for the first move
    } else delete ov.dataset.funImage;

    // ----------------------------
    // RELATED (interactive cards)
    // ----------------------------
    const relatedFromDoc = doc.querySelector(this.relatedSelector);
    relatedBox.innerHTML = '';
    if (relatedFromDoc) {
        const frag = document.createDocumentFragment();
        [...relatedFromDoc.childNodes].forEach(n => frag.appendChild(n.cloneNode(true)));
        relatedBox.appendChild(frag);
    }

    // ----------------------------
    // LEFT (title + visual content)
    // ----------------------------
    leftInner.innerHTML = '';

    // Title
    const titleText =
        (doc.querySelector(this.titleSelector)?.textContent?.trim()) ||
        card.getAttribute('aria-label') ||
        card.querySelector('.title')?.textContent?.trim() || '';

    if (titleText) {
        const titleWrap  = document.createElement('section');
        titleWrap.className = 'content-wrap';
        const titleBlock = document.createElement('div');
        titleBlock.className = 'content-block';
        const h1 = document.createElement('h1');
        h1.className = 'cf-title';
        h1.textContent = titleText;
        titleBlock.appendChild(h1);
        titleWrap.appendChild(titleBlock);
        leftInner.appendChild(titleWrap);
    }

    // Visual content (images/sections)
    const visual = doc.querySelector(this.visualSelector);
    if (visual) {
        const clone = visual.cloneNode(true);
        // avoid duplicate page titles inside visual stream
        clone.querySelector('h1')?.remove();

        [...clone.children].forEach(child => {
        if (child.matches('.content-wrap') || child.matches('section') || child.matches('.card')) {
            leftInner.appendChild(child);
        }
        });
    }

    // ----------------------------
    // Reset scroll positions to TOP (instant "jump")
    // ----------------------------
    // Column scrollers inside overlay

     this._setOverlayScrollBehavior(ov, 'auto');
     this._jumpToTop(ov);

    // In case the page/body is a scroller on some layouts
    window.scrollTo(0, 0);

    // A11y: move focus to the new title without scrolling
    const newTitle = leftInner.querySelector('.cf-title');
    if (newTitle) {
        newTitle.setAttribute('tabindex', '-1');
        newTitle.focus({ preventScroll: true });
    }

    setTimeout(() => this._setOverlayScrollBehavior(ov, 'revert'), 180);

  }

  /** Once what's on screen in the overlay has loaded, fade the page back in. */
  async _revealOverlay() {
    await this.transition.ready(this.overlay || document);
    await this.transition.reveal();
  }

  async _activateFromCard(card, url) {
    if (this.overlay || !url) return;

    // Fade the deck to white, build the overlay already in its final state
    // while hidden, then fade the finished page in — no staged reveals.
    await this.transition.cover();

    const ov = document.createElement('div');
    ov.className = 'cardfocus is-in show-left';
    ov.innerHTML = `
      <div class="cardfocus__shade"></div>

      <div class="cardfocus__stage">
        <!-- Fixed Back button -->
        <div class="stage--inner">
        <div class="cardfocus__backbar is-in">
          <button class="cardfocus__back" type="button" aria-label="Back">
            <svg class="cardfocus__back-icon" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M13 7H1.5M6.5 2 1.5 7l5 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>

        <!-- Floating mobile info toggle -->
        <button type="button" class="cardfocus__info" data-info-toggle aria-expanded="false" aria-label="Project info"><span aria-hidden="true">i</span></button>

        <!-- Left column (title + images). Start invisible but laid out -->
        <div class="cardfocus__left">
          <div class="cardfocus__leftInner"></div>
        </div>

        <!-- Right panel -->
        <aside class="cardfocus__panel is-in" aria-live="polite">
          <div class="cardfocus__content"></div>
        </aside>
        </div>
        <div class="related">
        </div>

      </div>
    `;
    document.body.appendChild(ov);
    this.overlay = ov;

    // Dim deck + lock body scroll
    if (this.deck) this.deck.setAttribute('data-dim', 'true');
    document.documentElement.classList.add('is-cardfocus-open');

    // Shade closes via history back (keeps URL correct)
    ov.querySelector('.cardfocus__shade').addEventListener('click', () => window.history.back(), { once: true });

    this._prevUrl = window.location.href;
    this._prevTitle = this.getTitle();
    window.history.pushState({ cardfocus: true }, '', url);

    await this._populate(card, url);
    await this._revealOverlay();
  }

  /**
   * The panel is populated from fetched HTML, so the server-rendered
   * `has-no-info` class won't survive a swap. Re-derive it from what actually
   * landed in the panel, and let the left column take the full stage.
   */
  _syncInfoState(ov) {
    if (!ov) return;
    const host = ov.querySelector('.cardfocus__content');
    const hasInfo = !!host && host.textContent.trim().length > 0;
    ov.classList.toggle('has-no-info', !hasInfo);
  }

  /**
   * The panel is sticky inside the scrolling stage. When it's taller than the
   * stage (long About / Credits), sticking at the top would hide its end, so
   * the CSS clamps `top` to keep its bottom in view instead: it scrolls with
   * the page until the end of the text shows, then sticks. That needs the two
   * heights, kept current here as the text, images and window change.
   */
  _fitPanel(ov) {
    const panel = ov?.querySelector('.cardfocus__panel');
    const stage = ov?.querySelector('.cardfocus__stage');
    if (!panel || !stage || panel._fitObserver) return;
    const fit = () => {
      panel.style.setProperty('--panel-h', `${panel.offsetHeight}px`);
      panel.style.setProperty('--stage-h', `${stage.clientHeight}px`);
    };
    panel._fitObserver = new ResizeObserver(fit);
    panel._fitObserver.observe(panel);
    panel._fitObserver.observe(stage);
    fit();
  }

  _strip(root, selectors) {
    if (!root) return;
    selectors.forEach(sel => root.querySelectorAll(sel).forEach(el => el.remove()));
  }

  async _fetchHTML(url) {
    const res = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'fetch' }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  }

  _onPopState() {
    if (this.overlay) this._reallyClose();
  }

  close() {
    if (this.overlay) window.history.back();
  }

  async _reallyClose() {
    if (!this.overlay || this._closing) return;
    this._closing = true;

    await this.transition.cover();

    this.overlay.remove();
    this.overlay = null;
    if (this.deck) this.deck.removeAttribute('data-dim');
    document.documentElement.classList.remove('is-cardfocus-open');

    if (this._prevTitle) {
      this.setTitle(this._prevTitle);
      this._prevTitle = null;
    }

    if (this._prevUrl) {
      // In case we didn't arrive via back, restore explicitly.
      window.history.pushState({}, '', this._prevUrl);
      this._prevUrl = null;
    }

    this._closing = false;
    await this.transition.reveal();
  }
  // Disable smooth scrolling inside overlay to avoid "gliding" back down.
_setOverlayScrollBehavior(ov, mode /* 'auto' | 'revert' */) {
  if (!ov) return;
  if (mode === 'auto') {
    ov.__oldScrollBehavior = ov.style.scrollBehavior;
    ov.style.scrollBehavior = 'auto';
    // also kill any inherited smooth scroll on common scroll containers
    ov.querySelectorAll('.cardfocus__left, .cardfocus__panel').forEach(el => {
      el.__oldScrollBehavior = el.style.scrollBehavior;
      el.style.scrollBehavior = 'auto';
    });
  } else {
    ov.style.scrollBehavior = ov.__oldScrollBehavior ?? '';
    ov.querySelectorAll('.cardfocus__left, .cardfocus__panel').forEach(el => {
      el.style.scrollBehavior = el.__oldScrollBehavior ?? '';
    });
  }
}

// Bullet-proof jump to top with retries across frames & after images decode
_jumpToTop(ov) {
  if (!ov) return;

  const scrollers = [
    ov.querySelector('.cardfocus__stage'),
    ov.querySelector('.cardfocus__left'),
    ov.querySelector('.cardfocus__panel'),
  ].filter(Boolean);

  // Stop as soon as the visitor scrolls (or after a moment regardless): the
  // later retries wait on images, and lazy ones only load as they come into
  // view, so a late retry used to throw you back to the top mid-page.
  let stopped = false;
  const intents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
  const stop = () => {
    stopped = true;
    intents.forEach(t => window.removeEventListener(t, stop, true));
  };
  intents.forEach(t => window.addEventListener(t, stop, { capture: true, passive: true }));
  setTimeout(stop, 1500);

  const jump = () => {
    if (stopped) return;
    scrollers.forEach(el => {
      // Most reliable reset across browsers
      el.scrollTop = 0;
      if (el.scrollTo) el.scrollTo(0, 0);
    });
    // body/page, in case it became the scroller
    document.scrollingElement?.scrollTo(0, 0);
    window.scrollTo(0, 0);
  };

  // 1) immediate
  jump();
  // 2) next frame
  requestAnimationFrame(() => {
    jump();
    // 3) one more frame for layout/FOIT/CLS
    requestAnimationFrame(jump);
  });

  // 4) after images settle (decode/load can shift layout). Lazy images below
  // the fold won't load until scrolled to, so only wait on the eager ones.
  const imgs = [...ov.querySelectorAll('img:not([loading="lazy"])')].slice(0, 24); // cap for safety
  if (imgs.length) {
    let pending = imgs.length;
    const done = () => { if (--pending <= 0) jump(); };
    imgs.forEach(img => {
      if (img.complete) return done();
      // prefer decode when available
      if (typeof img.decode === 'function') {
        img.decode().then(done, done);
      } else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
  }

  // 5) final insurance after a brief timeout
  setTimeout(jump, 120);
}

_shouldBackGoHome() {
  // If no same-origin referrer, prefer Home link
  const ref = document.referrer;
  if (!ref) return true;
  try {
    const u = new URL(ref);
    return u.origin !== window.location.origin;
  } catch {
    return true;
  }
}

_getHomeHref() {
  return '/'; // customise if needed
}


}
